import React, { useState, useEffect, useCallback } from 'react';
import { Button, Modal, Form, Alert, Col } from 'react-bootstrap';
import './ScheduleModals.scss';

const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const AvailabilityRequestModal = ({ show, onHide, mode, editData, onSubmit, submitting, submitError }) => {
    const [dayMode, setDayMode] = useState('grouped');
    const [selectedDays, setSelectedDays] = useState([]);
    const [groupedStart, setGroupedStart] = useState('');
    const [groupedEnd, setGroupedEnd] = useState('');
    const [individualTimes, setIndividualTimes] = useState({});
    const [effectiveStart, setEffectiveStart] = useState('');
    const [effectiveEnd, setEffectiveEnd] = useState('');
    const [notes, setNotes] = useState('');
    const [errors, setErrors] = useState({});

    const resetForm = useCallback(() => {
        setDayMode('grouped');
        setSelectedDays([]);
        setGroupedStart('');
        setGroupedEnd('');
        setIndividualTimes({});
        setEffectiveStart('');
        setEffectiveEnd('');
        setNotes('');
        setErrors({});
    }, []);

    useEffect(() => {
        if (show) {
            if (mode === 'edit' && editData) {
                setEffectiveStart(editData.startDate || '');
                setEffectiveEnd(editData.endDate || '');
                setNotes(editData.notes || '');
                const days = editData.days || [];
                setSelectedDays(days.map((d) => d.dayOfWeek).sort());
                const allSame =
                    days.length > 0 &&
                    days.every((d) => d.startTime === days[0].startTime && d.endTime === days[0].endTime);
                if (allSame && days.length > 0) {
                    setDayMode('grouped');
                    setGroupedStart(days[0].startTime || '');
                    setGroupedEnd(days[0].endTime || '');
                    setIndividualTimes({});
                } else {
                    setDayMode('individual');
                    const timesMap = {};
                    days.forEach((d) => {
                        timesMap[d.dayOfWeek] = { startTime: d.startTime || '', endTime: d.endTime || '' };
                    });
                    setIndividualTimes(timesMap);
                    setGroupedStart('');
                    setGroupedEnd('');
                }
                setErrors({});
            } else {
                resetForm();
            }
        }
    }, [show, mode, editData, resetForm]);

    const toggleDay = (dayIndex) => {
        setSelectedDays((prev) =>
            prev.includes(dayIndex) ? prev.filter((d) => d !== dayIndex) : [...prev, dayIndex].sort()
        );
    };

    const updateIndividualTime = (dayIndex, field, value) => {
        setIndividualTimes((prev) => ({
            ...prev,
            [dayIndex]: { ...prev[dayIndex], [field]: value },
        }));
    };

    const validateForm = () => {
        const errs = {};
        if (!effectiveStart) errs.effectiveStart = 'Effective start date is required.';
        if (!effectiveEnd) errs.effectiveEnd = 'Effective end date is required.';
        if (effectiveStart && effectiveEnd && effectiveEnd < effectiveStart) {
            errs.effectiveEnd = 'End date must be on or after start date.';
        }
        if (selectedDays.length === 0) errs.days = 'At least one day must be selected.';

        if (dayMode === 'grouped') {
            if (!groupedStart) errs.groupedStart = 'Start time is required.';
            if (!groupedEnd) errs.groupedEnd = 'End time is required.';
            else if (groupedStart && groupedEnd && groupedEnd <= groupedStart)
                errs.groupedEnd = 'End time must be after start time.';
        } else {
            selectedDays.forEach((d) => {
                const times = individualTimes[d] || {};
                if (!times.startTime) errs[`day${d}Start`] = `${DAY_LABELS[d]} start time is required.`;
                if (!times.endTime) errs[`day${d}End`] = `${DAY_LABELS[d]} end time is required.`;
                else if (times.startTime && times.endTime && times.endTime <= times.startTime)
                    errs[`day${d}End`] = `${DAY_LABELS[d]} end time must be after start time.`;
            });
        }
        return errs;
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        const errs = validateForm();
        setErrors(errs);
        if (Object.keys(errs).length > 0) return;

        const days = selectedDays.map((d) => {
            if (dayMode === 'grouped') {
                return { dayOfWeek: d, startTime: groupedStart, endTime: groupedEnd };
            }
            const times = individualTimes[d] || {};
            return { dayOfWeek: d, startTime: times.startTime, endTime: times.endTime };
        });

        onSubmit({
            startDate: effectiveStart,
            endDate: effectiveEnd,
            notes: notes || null,
            days,
        });
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg" className="schedule-modal">
            <Modal.Header closeButton>
                <Modal.Title>{mode === 'edit' ? 'Edit Availability Request' : 'New Availability Request'}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleFormSubmit}>
                <Modal.Body>
                    {submitError && <Alert variant="danger">{submitError}</Alert>}

                    <div className="row mb-3">
                        <Col xs={12} md={6} className="mb-3 mb-md-0">
                            <Form.Group>
                                <Form.Label>
                                    Effective Start Date <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="date"
                                    value={effectiveStart}
                                    onChange={(e) => setEffectiveStart(e.target.value)}
                                    isInvalid={!!errors.effectiveStart}
                                />
                                {errors.effectiveStart && (
                                    <Form.Control.Feedback type="invalid">
                                        {errors.effectiveStart}
                                    </Form.Control.Feedback>
                                )}
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Group>
                                <Form.Label>
                                    Effective End Date <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="date"
                                    value={effectiveEnd}
                                    onChange={(e) => setEffectiveEnd(e.target.value)}
                                    isInvalid={!!errors.effectiveEnd}
                                />
                                {errors.effectiveEnd && (
                                    <Form.Control.Feedback type="invalid">{errors.effectiveEnd}</Form.Control.Feedback>
                                )}
                            </Form.Group>
                        </Col>
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Label>
                            Schedule Mode <span className="text-danger">*</span>
                        </Form.Label>
                        <div className="d-flex flex-column flex-md-row">
                            <Form.Check
                                className="mr-md-3 mb-1 mb-md-0"
                                type="radio"
                                id={`schedule-${mode}-mode-grouped`}
                                label="Same hours for selected days"
                                checked={dayMode === 'grouped'}
                                onChange={() => setDayMode('grouped')}
                            />
                            <Form.Check
                                type="radio"
                                id={`schedule-${mode}-mode-individual`}
                                label="Different hours per day"
                                checked={dayMode === 'individual'}
                                onChange={() => setDayMode('individual')}
                            />
                        </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>
                            Select Available Days <span className="text-danger">*</span>
                        </Form.Label>
                        {errors.days && <div className="text-danger small mb-1">{errors.days}</div>}
                        <div className="d-flex flex-wrap">
                            {DAY_LABELS.map((label, idx) => (
                                <Button
                                    key={idx}
                                    size="sm"
                                    variant={selectedDays.includes(idx) ? 'primary' : 'outline-secondary'}
                                    className="schedule-modal__day-btn mr-1 mb-1"
                                    onClick={() => toggleDay(idx)}
                                >
                                    {label.slice(0, 3)}
                                </Button>
                            ))}
                        </div>
                    </Form.Group>

                    {dayMode === 'grouped' && selectedDays.length > 0 && (
                        <div className="row mb-3">
                            <Col xs={12} md={6} className="mb-3 mb-md-0">
                                <Form.Group>
                                    <Form.Label>
                                        Start Time <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="time"
                                        value={groupedStart}
                                        onChange={(e) => setGroupedStart(e.target.value)}
                                        isInvalid={!!errors.groupedStart}
                                    />
                                    {errors.groupedStart && (
                                        <Form.Control.Feedback type="invalid">
                                            {errors.groupedStart}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col xs={12} md={6}>
                                <Form.Group>
                                    <Form.Label>
                                        End Time <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="time"
                                        value={groupedEnd}
                                        onChange={(e) => setGroupedEnd(e.target.value)}
                                        isInvalid={!!errors.groupedEnd}
                                    />
                                    {errors.groupedEnd && (
                                        <Form.Control.Feedback type="invalid">
                                            {errors.groupedEnd}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                        </div>
                    )}

                    {dayMode === 'individual' && selectedDays.length > 0 && (
                        <div className="mb-3">
                            {selectedDays.map((dayIdx) => (
                                <div key={dayIdx} className="schedule-modal__individual-day mb-2">
                                    <Form.Label className="mb-1 fw-bold schedule-modal__day-label">
                                        {DAY_LABELS[dayIdx]}
                                    </Form.Label>
                                    <div className="d-flex align-items-center">
                                        <Form.Control
                                            type="time"
                                            size="sm"
                                            value={individualTimes[dayIdx]?.startTime || ''}
                                            onChange={(e) => updateIndividualTime(dayIdx, 'startTime', e.target.value)}
                                            isInvalid={!!errors[`day${dayIdx}Start`]}
                                        />
                                        <span className="mx-2">to</span>
                                        <Form.Control
                                            type="time"
                                            size="sm"
                                            value={individualTimes[dayIdx]?.endTime || ''}
                                            onChange={(e) => updateIndividualTime(dayIdx, 'endTime', e.target.value)}
                                            isInvalid={!!errors[`day${dayIdx}End`]}
                                        />
                                    </div>
                                    {(errors[`day${dayIdx}Start`] || errors[`day${dayIdx}End`]) && (
                                        <div className="text-danger small mt-1">
                                            {errors[`day${dayIdx}Start`] || errors[`day${dayIdx}End`]}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>Notes</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Optional notes…"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={submitting} className="schedule-modal__btn">
                        {mode === 'edit' ? 'Back' : 'Cancel'}
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={submitting}
                        className="schedule-modal__btn schedule-modal__btn--primary"
                    >
                        {submitting
                            ? mode === 'edit'
                                ? 'Saving…'
                                : 'Submitting…'
                            : mode === 'edit'
                            ? 'Save Changes'
                            : 'Submit Request'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default AvailabilityRequestModal;
